import { z } from 'zod';
export const notificationSeveritySchema = z.enum(['info', 'success', 'warning', 'error']);
export const NotificationSeverityValues = notificationSeveritySchema.enum;
export const partnerCockpitNotificationTypeSchema = z.enum([
    'PartnerCockpit.TeamMemberJoined',
    'PartnerCockpit.ContactRequestAccepted',
    'PartnerCockpit.EngagementChatCustomerMessaged',
    'PartnerCockpit.SubscriptionCheckoutCompleted',
    'PartnerCockpit.ProfileCompletionReminder',
]);
// Targets that need no entity to route to — a fixed settings page, no per-instance id.
export const staticNotificationActionTargetSchema = z.enum(['teamMembers', 'companySettings', 'verification']);
// Targets that route to one specific instance and therefore require its uuid.
const entityNotificationActionTargetSchema = z.enum(['openRequestDetail']);
// Opens an arbitrary external URL (e.g. an explainer video) in a new tab — the only target that carries
// its own destination rather than resolving to an internal route. Restricted to http(s): the value is
// bound directly to an anchor href with no further sanitisation, so a javascript: URL would execute on
// click — a stored-XSS vector, since ops-authored announcements target every matching partner.
export const externalLinkNotificationActionSchema = z.object({
    target: z.literal('externalLink'),
    url: z.url({ protocol: /^https?$/ }),
});
// A discriminated union so `entityUuid`/`url` are only present (and required) on targets that actually
// need them — a target without an entity or URL to point at can never be constructed with one missing.
export const notificationActionSchema = z.discriminatedUnion('target', [
    z.object({ target: staticNotificationActionTargetSchema }),
    z.object({ target: entityNotificationActionTargetSchema, entityUuid: z.uuid() }),
    externalLinkNotificationActionSchema,
]);
export const NotificationActionTargetValues = {
    ...staticNotificationActionTargetSchema.enum,
    ...entityNotificationActionTargetSchema.enum,
    externalLink: 'externalLink',
};
export const notificationContentSchema = z.object({
    titleKey: z.string(),
    descriptionKey: z.string(),
    params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
});
export const partnerCockpitNotificationSchema = z.object({
    uuid: z.uuid(),
    type: partnerCockpitNotificationTypeSchema,
    severity: notificationSeveritySchema,
    content: notificationContentSchema,
    action: notificationActionSchema.nullable(),
    read: z.boolean(),
    createdAt: z.iso.datetime(),
});
export const partnerCockpitNotificationsPageSchema = z.object({
    items: z.array(partnerCockpitNotificationSchema),
    total: z.number().int(),
});
export const partnerCockpitNotificationStatusFilterSchema = z.enum(['all', 'unread', 'read']);
export const PartnerCockpitNotificationStatusFilterValues = partnerCockpitNotificationStatusFilterSchema.enum;
export const partnerCockpitNotificationsQuerySchema = z.object({
    status: partnerCockpitNotificationStatusFilterSchema.default('all'),
    start: z.coerce.number().int().min(0).default(0),
    pageSize: z.coerce.number().int().min(1).max(100).default(10),
});
export const partnerCockpitNotificationsUnreadCountSchema = z.object({
    count: z.number().int(),
});
export const RECENT_NOTIFICATIONS_LIMIT = 5;
